import { Button, Card, EmptyState, FormField, Input, Select, SkeletonText } from '@ashniva/ui';
import { useState, type FormEvent } from 'react';

import { useRiSearchQuery } from '../api-code';
import { linesLabel } from '../ri-text';
import { useWorkspace } from '../workspace-context';
import { ProblemNotice } from './ProblemNotice';

const LIMITS = [
  { value: '10', label: '10 results' },
  { value: '20', label: '20 results' },
];

/** Search the indexed code (P14-04): one request per search, results with path, symbol, lines and the snippet. */
export function SearchPanel() {
  const { linkId, openCode } = useWorkspace();
  const [draft, setDraft] = useState('');
  const [limit, setLimit] = useState('10');
  const [submitted, setSubmitted] = useState<{ query: string; limit: number } | null>(null);
  const search = useRiSearchQuery(linkId, submitted?.query ?? '', submitted?.limit ?? 10);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const query = draft.trim();
    if (query) {
      setSubmitted({ query, limit: Number(limit) });
    }
  };

  return (
    <div className="ri-stack">
      <Card title="Search the code">
        <form className="ri-form" onSubmit={onSubmit} role="search" aria-label="Search the code">
          <FormField
            label="What to look for"
            hint="Words, a function or class name, an error message."
          >
            <Input
              type="search"
              value={draft}
              maxLength={200}
              onChange={(event) => setDraft(event.target.value)}
            />
          </FormField>
          <div className="ri-row">
            <FormField label="How many">
              <Select
                value={limit}
                options={LIMITS}
                onChange={(event) => setLimit(event.target.value)}
              />
            </FormField>
            <Button
              type="submit"
              variant="primary"
              disabled={!draft.trim()}
              loading={search.isFetching}
            >
              Search
            </Button>
          </div>
        </form>
      </Card>
      {submitted ? renderResults() : null}
    </div>
  );

  function renderResults() {
    if (search.isLoading) {
      return (
        <div aria-busy="true">
          <span className="sr-only">Searching</span>
          <SkeletonText lines={6} />
        </div>
      );
    }
    if (search.isError || !search.data) {
      return <ProblemNotice error={search.error} onRetry={() => void search.refetch()} />;
    }
    const hits = search.data.hits;
    if (hits.length === 0) {
      return (
        <EmptyState
          icon="○"
          title="No matches"
          description="Try fewer words, a function or class name, or another spelling. Only indexed files are searched."
        />
      );
    }
    return (
      <section aria-label="Search results">
        <p className="ri-muted" role="status">
          {hits.length === 1 ? 'One match' : `${hits.length} matches`}
        </p>
        <ol className="ri-list">
          {hits.map((hit, index) => (
            <li key={`${hit.path}:${hit.startLine}:${index}`} className="ri-list__item">
              <div className="ri-row">
                <span className="ri-mono">{hit.path}</span>
                <span className="ri-muted">{linesLabel(hit.startLine, hit.endLine)}</span>
                {hit.symbolName ? <span className="ri-mono">{hit.symbolName}</span> : null}
                <span className="ri-muted">{hit.kind}</span>
              </div>
              <pre className="ri-snippet">{hit.content}</pre>
              <div>
                <Button
                  size="sm"
                  onClick={() =>
                    openCode({ path: hit.path, start: hit.startLine, end: hit.endLine })
                  }
                  aria-label={`Open ${hit.path}, ${linesLabel(hit.startLine, hit.endLine)}`}
                >
                  Open
                </Button>
              </div>
            </li>
          ))}
        </ol>
      </section>
    );
  }
}
